import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import { useOffersAddonContext } from '@/context/OffersAddonProvider'
import { useOffersVehicleContext } from '@/context/OffersVehicleProvider'
import { usePartner } from '@/context/PartnerContext'
import { useDisclosure } from '@chakra-ui/react'
import { useTranslationsContext } from 'BrokerTsLib/src/context/TranslationsContext'
import type { Offer, OfferVehicle } from 'BrokerTsLib/src/dtos'
import { Risk } from 'BrokerTsLib/src/enums'
import { priceFormat } from 'BrokerTsLib/src/Utils/formatPrice'
import { useEffect, useRef, useState } from 'react'
import { useInsurerPaymentFrequencies } from '.'
import { useConfirm } from './useOffer'

export const useConfirmOffers = () => {
  const { translate, translateFormat } = useTranslationsContext()
  const { currency } = usePartner()
  const { mutateAsync: confirmOffer } = useConfirm()
  const { updateOffer } = useOffersVehicleContext()
  const { refetchInsuranceParams } = useInsuranceParams()
  const { data: dataPf } = useInsurerPaymentFrequencies()
  const { offers: addonOffers } = useOffersAddonContext()

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessages, setErrorMessages] = useState<string[]>([])
  const [priceChangeError, setPriceChangeError] = useState<string | null>(null)

  const {
    isOpen: isOpenErrorDialog,
    onOpen: openErrorDialog,
    onClose: onCloseErrorDialog,
  } = useDisclosure()
  const {
    isOpen: isOpenPriceChangeDialog,
    onOpen: onOpenPriceChangeDialog,
    onClose: onClosePriceChangeDialog,
  } = useDisclosure()

  const pendingSuccessRef = useRef<(() => void) | null>(null)

  useEffect(() => {
    if (errorMessages.length > 0) openErrorDialog()
  }, [errorMessages])

  const getTotalPrice = (offer: Offer): number => {
    const pf = dataPf?.data
      .find((x) => x.insurerId === offer.insurerId)
      ?.paymentFrequencies.find((x) => x.id === offer.paymentFrequencyId)
    const paymentCount = pf?.value ? 12 / parseInt(pf.value) : 0

    const linkedOfferAddon = addonOffers
      .filter((x) => x.riskId === Risk.GAP)
      .find((x) => x.linkedOfferId?.includes(offer.offerId))
    const linkedAddonPricePerYear = linkedOfferAddon?.pricePerYear ?? 0

    if (offer.priceFrequency)
      return offer.priceFrequency * paymentCount

    // importovane nemusi mit frekvenci
    return offer.price + linkedAddonPricePerYear
  }

  const collectConfirmErrors = (errs: any): string[] => {
    const responseData = Array.isArray(errs.response?.data) ? errs.response?.data : []
    if (responseData.length > 0)
      return responseData.map((e: any) => e.exception?.message ?? e.message)
    return [translate('quoteItem.sidebar.offerSelectionError', 'Nastala chyba při výběru kalkulace')]
  }

  // Confirm offers without subsequent action (leasing "save" flow – ignoreCallback path).
  const confirmOffersOnly = async (offers: Offer[]) => {
    setIsSubmitting(true)
    const errors: string[] = []

    for (const offer of offers) {
      try {
        const { data } = await confirmOffer(offer.offerId)
        const newPrice = getTotalPrice(data)

        if (isNaN(newPrice) || newPrice === 0) {
          errors.push(translate('quoteItem.sidebar.offerSelectionError', 'Nastala chyba při výběru kalkulace'))
          continue
        }

        updateOffer(data as OfferVehicle)
      } catch (errs: any) {
        errors.push(...collectConfirmErrors(errs))
      }
    }

    if (errors.length > 0) setErrorMessages(errors)
    refetchInsuranceParams()
    setIsSubmitting(false)
  }

  // Confirm offers with price-change detection; calls onSuccess when all pass.
  const confirmOffers = async (offers: Offer[], onSuccess: () => void) => {
    setIsSubmitting(true)
    pendingSuccessRef.current = onSuccess

    const errors: string[] = []
    const priceChanges: string[] = []

    for (const offer of offers) {
      const oldPrice = getTotalPrice(offer)

      try {
        const { data } = await confirmOffer(offer.offerId)
        const newPrice = getTotalPrice(data)

        if (isNaN(newPrice) || newPrice === 0) {
          errors.push(translate('quoteItem.sidebar.offerSelectionError', 'Nastala chyba při výběru kalkulace'))
          continue
        }

        if (oldPrice !== newPrice) {
          priceChanges.push(
            translateFormat(
              'quoteItem.sidebar.offerPriceChanged',
              'Cena kalkulace se změnila z původní ceny {oldPrice} na {newPrice}',
              {
                oldPrice: priceFormat(oldPrice, currency?.code),
                newPrice: priceFormat(newPrice, currency?.code),
              },
            ) as string,
          )
        }

        updateOffer(data as OfferVehicle)
      } catch (errs: any) {
        errors.push(...collectConfirmErrors(errs))
      }
    }

    refetchInsuranceParams()

    if (errors.length > 0) {
      setErrorMessages(errors)
      setIsSubmitting(false)
      return
    }

    if (priceChanges.length > 0) {
      setPriceChangeError(priceChanges.join('\n'))
      onOpenPriceChangeDialog()
      setIsSubmitting(false)
      return
    }

    onSuccess()
  }

  const handleConfirmPriceChange = () => {
    onClosePriceChangeDialog()
    pendingSuccessRef.current?.()
  }

  return {
    confirmOffers,
    confirmOffersOnly,
    isSubmitting,
    setIsSubmitting,
    setErrorMessages,
    errorDialogState: {
      isOpen: isOpenErrorDialog,
      messages: errorMessages,
      onClose: onCloseErrorDialog,
    },
    priceChangeDialogState: {
      isOpen: isOpenPriceChangeDialog,
      message: priceChangeError,
      onClose: onClosePriceChangeDialog,
      onConfirm: handleConfirmPriceChange,
    },
  }
}
